import { useEffect, useState } from "react";
import { Loader2, Megaphone, Trash2, Send, Clock, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

export default function AnnouncementsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", message: "" });
  const [schedule, setSchedule] = useState(false);
  const [when, setWhen] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/announcements").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title || !form.message) return toast.error("Title and message required");
    if (schedule && !when) return toast.error("Pick a date and time");
    if (schedule && new Date(when) <= new Date()) return toast.error("Scheduled time must be in the future");
    setSending(true);
    try {
      const payload = { ...form, scheduled_at: schedule && when ? new Date(when).toISOString() : null };
      await api.post("/announcements", payload);
      toast.success(schedule ? "Announcement scheduled" : "Announcement published to hub");
      setForm({ title: "", message: "" });
      setWhen("");
      setSchedule(false);
      load();
    } catch { toast.error("Could not publish"); }
    finally { setSending(false); }
  };

  const del = async (id) => { await api.delete(`/announcements/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Announcements</h1>
        <p className="text-muted-foreground text-sm">Publish now or schedule updates to send automatically — pushed to guests who opted in.</p>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-4">
        <h3 className="font-serif text-lg text-foreground">New announcement</h3>
        <div><Label>Title</Label><Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="Sunset dinner service tonight" /></div>
        <div><Label>Message</Label><Textarea data-testid="announcement-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="mt-1.5" placeholder="Join us on the terrace from 7pm…" /></div>
        <div className="flex items-center justify-between border border-border rounded-md p-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-accent" />
            <Label className="cursor-pointer">Schedule for later</Label>
          </div>
          <Switch checked={schedule} onCheckedChange={setSchedule} data-testid="schedule-toggle" />
        </div>
        {schedule && (
          <div><Label>Send at</Label><Input data-testid="schedule-datetime" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1.5" /></div>
        )}
        <Button onClick={send} disabled={sending} data-testid="publish-announcement-btn" className="bg-primary text-primary-foreground">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {schedule ? "Schedule announcement" : "Publish to hub"}</>}
        </Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Megaphone className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No announcements yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="bg-card border border-border rounded-md p-4 flex items-start justify-between" data-testid={`announcement-${a.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground font-medium">{a.title}</p>
                  {a.status === "scheduled" ? (
                    <Badge className="bg-amber-100 text-amber-800 border-0 flex items-center gap-1"><Clock className="w-3 h-3" /> Scheduled</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-800 border-0">Published</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {a.status === "scheduled" && a.scheduled_at ? `Sends ${new Date(a.scheduled_at).toLocaleString()}` : new Date(a.created_at).toLocaleString()}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => del(a.id)} data-testid={`delete-announcement-${a.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
